import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader, KPIGrid, DataTable, StandardModal, StatusBadge,
  FilterPills, Alert, Btn, FormField,
  selectStyle, textareaStyle,
  colors, spacing, typography, radius,
} from "../design-system";

const BASE        = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const ACC_API     = `${BASE}/course-accreditations`;
const COURSES_API = `${BASE}/accredited-courses`;

export default function CourseAccreditations() {
  const navigate = useNavigate();
  const [accreditations, setAccreditations] = useState([]);
  const [courses, setCourses]               = useState([]);
  const [loading, setLoading]               = useState(true);

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCourse, setFilterCourse] = useState("");

  const [showCreate,  setShowCreate]  = useState(false);
  const [showConfirm, setShowConfirm] = useState(null);   // { id, action, label }
  const [confirmNotes, setConfirmNotes] = useState("");
  const [createForm, setCreateForm]   = useState({ course_id: "", notes: "" });
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState("");
  const [msgType, setMsgType]         = useState("success");

  const token    = localStorage.getItem("token");
  const userRole = JSON.parse(localStorage.getItem("user") || "{}").role;
  const isSuperAdmin = userRole === "super_admin";
  const headers  = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const fetchAccreditations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterCourse) params.set("course_id", filterCourse);
      const res = await fetch(`${ACC_API}/?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to load accreditations");
      const data = await res.json();
      setAccreditations(Array.isArray(data) ? data : []);
    } catch (e) {
      flash("Failed to load accreditations: " + e.message, "error");
      setAccreditations([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCourse]);

  useEffect(() => { fetchAccreditations(); }, [fetchAccreditations]);

  useEffect(() => {
    fetch(`${COURSES_API}/`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCourses(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(""), 3500); };

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${ACC_API}/`, {
        method: "POST", headers,
        body: JSON.stringify({ course_id: parseInt(createForm.course_id), notes: createForm.notes || null }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Accreditation record created.");
      setShowCreate(false);
      setCreateForm({ course_id: "", notes: "" });
      fetchAccreditations();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleAction(id, action) {
    setSaving(true);
    try {
      const body = confirmNotes ? { notes: confirmNotes } : {};
      const res = await fetch(`${ACC_API}/${id}/${action}`, {
        method: "PATCH", headers, body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash(`Course accreditation ${action}d.`);
      setShowConfirm(null);
      setConfirmNotes("");
      fetchAccreditations();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  // KPIs
  const expiringSoon = accreditations.filter(a => {
    if (!a.expires_at) return false;
    const d = (new Date(a.expires_at) - new Date()) / (1000 * 86400);
    return d > 0 && d <= 90;
  }).length;

  const kpis = [
    { label: "Total Records",    value: accreditations.length,                                      icon: "📋", color: colors.primary },
    { label: "Approved",         value: accreditations.filter(a => a.status === "approved").length, icon: "✅", color: colors.success },
    { label: "Pending Review",   value: accreditations.filter(a => a.status === "pending").length,  icon: "⏳", color: colors.warning },
    { label: "Expiring (90d)",   value: expiringSoon, icon: "⚠️", color: expiringSoon > 0 ? colors.warning : colors.success },
  ];

  const statusOptions = [
    { value: "all",       label: "All Status" },
    { value: "pending",   label: "Pending" },
    { value: "approved",  label: "Approved" },
    { value: "suspended", label: "Suspended" },
    { value: "revoked",   label: "Revoked" },
    { value: "expired",   label: "Expired" },
  ];

  const columns = [
    {
      key: "id", label: "#",
      render: v => <span style={{ color: colors.textMuted, fontFamily: "monospace" }}>#{v}</span>,
    },
    {
      key: "course_title", label: "Course",
      render: (v, row) => <span style={{ fontWeight: typography.fontWeights.semibold }}>{v || `Course #${row.course_id}`}</span>,
    },
    {
      key: "status", label: "Status",
      render: v => <StatusBadge status={v} />,
    },
    {
      key: "issued_at", label: "Issued",
      render: v => v ? new Date(v).toLocaleDateString() : "—",
    },
    {
      key: "expires_at", label: "Expires",
      render: (v, row) => {
        if (!v) return "—";
        const isExpired = row.status === "expired";
        const daysLeft  = Math.ceil((new Date(v) - new Date()) / (1000 * 86400));
        const color = isExpired ? colors.danger : (daysLeft <= 90 ? colors.warning : colors.textPrimary);
        return (
          <span style={{ color }}>
            {new Date(v).toLocaleDateString()}
            {!isExpired && daysLeft <= 90 && daysLeft > 0 && (
              <span style={{ display: "block", fontSize: typography.fontSizes.xs, color: colors.warning }}>{daysLeft}d left</span>
            )}
          </span>
        );
      },
    },
    {
      key: "id", label: "Evidence",
      render: (v) => (
        <Btn size="sm" variant="ghost" onClick={() => navigate(`/accreditation/course-accreditations/${v}/evidence`)}>
          Manage
        </Btn>
      ),
    },
    {
      key: "notes", label: "Notes",
      render: v => v
        ? <span title={v} style={{ color: colors.textSecondary }}>{v.slice(0, 40)}{v.length > 40 ? "…" : ""}</span>
        : "—",
    },
    {
      key: "_actions", label: "", align: "right",
      render: (_, acc) => {
        if (!isSuperAdmin) return null;
        return (
          <div style={{ display: "flex", gap: spacing.xs, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <Btn size="sm" variant="primary" onClick={() => navigate(`/accreditation/course-accreditations/${acc.id}/decision`)}>
              Decision Panel
            </Btn>
            {acc.status === "pending" && (
              <Btn size="sm" variant="success" onClick={() => { setShowConfirm({ id: acc.id, action: "approve", label: "Approve" }); setConfirmNotes(""); }}>Approve</Btn>
            )}
            {acc.status === "approved" && (
              <Btn size="sm" variant="warning" onClick={() => { setShowConfirm({ id: acc.id, action: "suspend", label: "Suspend" }); setConfirmNotes(""); }}>Suspend</Btn>
            )}
            {acc.status === "suspended" && (
              <Btn size="sm" variant="success" onClick={() => { setShowConfirm({ id: acc.id, action: "reinstate", label: "Reinstate" }); setConfirmNotes(""); }}>Reinstate</Btn>
            )}
            {!["revoked", "expired"].includes(acc.status) && (
              <Btn size="sm" variant="danger" onClick={() => { setShowConfirm({ id: acc.id, action: "revoke", label: "Revoke" }); setConfirmNotes(""); }}>Revoke</Btn>
            )}
            <Btn size="sm" variant="ghost" onClick={() => navigate(`/accreditation/course-accreditations/${acc.id}/evidence`)}>Evidence</Btn>
          </div>
        );
      },
    },
  ];

  const actionMessage = {
    approve:   "Approving will set the accreditation to approved and set expiry to 36 months from today.",
    suspend:   "This will suspend the course accreditation pending review.",
    revoke:    "This will permanently revoke the course accreditation.",
    reinstate: "This will reinstate the suspended accreditation.",
  };

  return (
    <div>
      <PageHeader
        title="Course Accreditations"
        subtitle="Manage ICSC accreditation records for governance course definitions."
        actions={isSuperAdmin && <Btn onClick={() => setShowCreate(true)}>+ Create Accreditation</Btn>}
      />

      <KPIGrid kpis={kpis} columns={4} />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg("")} />}

      {/* Filters */}
      <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.lg, flexWrap: "wrap", alignItems: "center" }}>
        <FilterPills options={statusOptions} value={filterStatus} onChange={setFilterStatus} />
        <select value={filterCourse} onChange={e => setFilterCourse(e.target.value)} style={{ ...selectStyle, width: "auto" }}>
          <option value="">All Courses</option>
          {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={accreditations}
        loading={loading}
        emptyMessage="No accreditation records found."
        keyField="id"
      />

      {/* Create Modal */}
      {showCreate && (
        <StandardModal
          title="Create Course Accreditation"
          onClose={() => setShowCreate(false)}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Btn>
              <Btn type="submit" form="create-acc-form" loading={saving}>Create Record</Btn>
            </>
          }
        >
          <form id="create-acc-form" onSubmit={handleCreate}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <FormField label="Course" required>
                <select
                  required
                  value={createForm.course_id}
                  onChange={e => setCreateForm(f => ({ ...f, course_id: e.target.value }))}
                  style={selectStyle}
                >
                  <option value="">Select course...</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </FormField>
              <FormField label="Notes">
                <textarea
                  value={createForm.notes}
                  onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  style={textareaStyle}
                />
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}

      {/* Confirm Action Modal */}
      {showConfirm && (
        <StandardModal
          title={`${showConfirm.label} Accreditation #${showConfirm.id}`}
          onClose={() => { setShowConfirm(null); setConfirmNotes(""); }}
          danger={showConfirm.action === "revoke"}
          footer={
            <>
              <Btn variant="secondary" onClick={() => { setShowConfirm(null); setConfirmNotes(""); }}>Cancel</Btn>
              <Btn
                variant={showConfirm.action === "revoke" ? "danger" : "primary"}
                onClick={() => handleAction(showConfirm.id, showConfirm.action)}
                loading={saving}
              >
                {showConfirm.label}
              </Btn>
            </>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
            <p style={{ color: colors.textSecondary, margin: 0 }}>
              {actionMessage[showConfirm.action]}
            </p>
            <FormField label="Notes (optional)">
              <textarea
                value={confirmNotes}
                onChange={e => setConfirmNotes(e.target.value)}
                rows={3}
                style={textareaStyle}
              />
            </FormField>
          </div>
        </StandardModal>
      )}

    </div>
  );
}
